import * as React from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Badge } from './badge';
import { Button } from './button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

interface MultiComboboxProps {
    options: string[];
    value: string[];
    onChange: (value: string[]) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    className?: string;
    'aria-label'?: string;
}

export function MultiCombobox({
    options = [],
    value,
    onChange,
    placeholder = 'Select...',
    searchPlaceholder = 'Search...',
    emptyText = 'No results found.',
    className,
    'aria-label': ariaLabel,
}: MultiComboboxProps) {
    const [open, setOpen] = React.useState(false);

    const handleSelect = (option: string) => {
        if (value.includes(option)) {
            onChange(value.filter((v) => v !== option));
        } else {
            onChange([...value, option]);
        }
    };

    const handleRemove = (option: string, e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(value.filter((v) => v !== option));
    };

    const maxVisible = 2;
    const visibleTags = value.slice(0, maxVisible);
    const overflowCount = value.length - maxVisible;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-label={ariaLabel}
                    className={cn(
                        'h-10 w-full justify-between px-3 py-1.5 text-sm font-normal leading-tight shadow-sm hover:bg-transparent focus:ring-1 focus:ring-ring',
                        !value.length && 'text-muted-foreground',
                        className
                    )}
                >
                    {value.length === 0 ? (
                        <span>{placeholder}</span>
                    ) : (
                        <div className="flex items-center gap-1 overflow-hidden mr-1">
                            {visibleTags.map((v) => (
                                <Badge
                                    key={v}
                                    variant="secondary"
                                    className="gap-0.5 pr-0.5 text-xs h-5 font-normal bg-zinc-200 text-zinc-800 border-0 hover:bg-zinc-300 min-w-0"
                                >
                                    <span className="truncate">{v}</span>
                                    <button
                                        className="ml-0.5 rounded-full p-0.5 outline-none hover:bg-muted-foreground/20 shrink-0"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={(e) => handleRemove(v, e)}
                                    >
                                        <X className="h-2.5 w-2.5" />
                                    </button>
                                </Badge>
                            ))}
                            {overflowCount > 0 && (
                                <span className="text-xs text-muted-foreground shrink-0">
                                    +{overflowCount}
                                </span>
                            )}
                        </div>
                    )}
                    <div className="ml-auto flex items-center shrink-0">
                        {value.length > 0 && (
                            <button
                                className="rounded-full p-0.5 outline-none hover:bg-muted-foreground/20 mr-1"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onChange([]);
                                }}
                            >
                                <X className="h-3.5 w-3.5 opacity-50" />
                            </button>
                        )}
                        <ChevronsUpDown className="h-4 w-4 opacity-50" />
                    </div>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                <Command>
                    <CommandInput placeholder={searchPlaceholder} />
                    <CommandList>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => (
                                <CommandItem
                                    key={option}
                                    value={option}
                                    onSelect={() => handleSelect(option)}
                                    className="hover:bg-accent hover:text-accent-foreground data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                                >
                                    <div
                                        className={cn(
                                            'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                                            value.includes(option)
                                                ? 'bg-primary text-primary-foreground'
                                                : 'opacity-50'
                                        )}
                                    >
                                        <Check
                                            className={cn(
                                                'h-3 w-3',
                                                value.includes(option)
                                                    ? 'opacity-100'
                                                    : 'opacity-0'
                                            )}
                                        />
                                    </div>
                                    {option}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
